import React from 'react';

import {
  FlexContainer,
  Heading,
  HeadingSize,
  Text,
} from '@user-interviews/ui-design-system';

import { mosaicIllustration } from 'lib/mosaic_assets';
import { routes } from 'lib/routes';

import * as styles from './link_button.module.css';

export const AuthenticationType = {
  Researcher: 'Researcher',
  Participant: 'Participant',
} as const;

type AuthenticationTypeValue =
  (typeof AuthenticationType)[keyof typeof AuthenticationType];

type LinkButtonProps = {
  authenticationType: AuthenticationTypeValue;
  isSignin: boolean;
};

function getLinkButtonAttributes({
  authenticationType,
  isSignin,
}: LinkButtonProps) {
  if (!(authenticationType in AuthenticationType)) {
    throw new Error(`invalid authentication type: ${authenticationType}`);
  }

  const {
    illustrationCategory,
    illustrationZoom,
    signInPath,
    signupPath,
    subtitle,
    title,
  } = {
    [AuthenticationType.Participant]: {
      illustrationCategory: 'participant' as const,
      illustrationZoom: 1.22,
      signInPath: routes.participant_signin_path(),
      signupPath: routes.participant_signup_path(),
      subtitle:
        'Earn money participating in research studies and help shape real products',
      title: 'Participant',
    },
    [AuthenticationType.Researcher]: {
      illustrationCategory: 'researcher' as const,
      illustrationZoom: 1.18,
      signInPath: routes.account_signin_path(),
      signupPath: routes.account_signup_path(),
      subtitle:
        'Create research projects and recruit quality participants, fast',
      title: 'Researcher',
    },
  }[authenticationType];

  return {
    illustrationCategory,
    illustrationZoom,
    subtitle,
    title,
    path: isSignin ? signInPath : signupPath,
  };
}

export function LinkButton({ authenticationType, isSignin }: LinkButtonProps) {
  const { illustrationCategory, illustrationZoom, path, subtitle, title } =
    getLinkButtonAttributes({ authenticationType, isSignin });
  const image = mosaicIllustration(illustrationCategory, 'sign-in', {
    zoom: illustrationZoom,
  });

  return (
    <a
      aria-label={`${isSignin ? 'Sign in' : 'Sign up'} as a ${title}. ${subtitle}`}
      className={styles.linkButton}
      href={path}
    >
      <Heading level={2} size={HeadingSize.MEDIUM}>
        {title}
      </Heading>
      <Text className={styles.subtitle}>{subtitle}</Text>
      <FlexContainer
        alignItems="center"
        flexDirection="row"
        justifyContent="center"
      >
        <img alt="" src={image.src} style={image.style} />
      </FlexContainer>
    </a>
  );
}
